/**
 * Facility records (docs/facility-delivery/DESIGN.md): the desk inside Manifest Review. Every type mirrors what
 * backend/src/routes/facilityRecords.js and services/facilityRecords/* return. Its own file: manifestApi.ts is
 * another builder's surface.
 *
 * The public secure-link page (pages/records/RecordsLink.tsx) does NOT use this module or the shared client: it has
 * no session and must never send one.
 */
import { apiRequest } from '../../services/api';

const BASE = '/manifest-intake/facility-records';

export type Channel = 'EMAIL' | 'PCC_UPLOAD' | 'OTHER' | 'HOLD';
export type Artifacts = 'AUDIOGRAM' | 'VISIT_SUMMARY' | 'BOTH';
export type View = 'to_prepare' | 'ready_to_send' | 'to_upload' | 'awaiting_confirmation' | 'exceptions' | 'done';
export type Queue = 'pending' | 'ready' | 'sent' | 'confirmed' | 'failed' | 'held';
export type SourceKind = 'DOCUMENT' | 'VISIT_SUMMARY';

export interface Can { prepare: boolean; send: boolean; configure: boolean }
export interface Switches { writes: boolean; send: boolean }
/** What the server is set up for: sending needs a Reply-To mailbox (FACILITY_RECORDS_REPLY_TO). */
export interface Config { reply_to_configured: boolean }

export interface Reminder { delivered_on: string; business_days_waiting: number; due: boolean; escalated: boolean; last_reminded_at: string | null }

export interface DeskRow {
  key: string;
  facility_id: string;
  facility_name: string | null;
  visit_date: string;
  batch_id: string | null;
  channel: Channel | null;
  verified: boolean;
  counts: { in_batch: number; held: number; complete: number; missing: number; needs_decision: number; files: number; uploaded: number };
  completed: boolean;
  queue: Queue;
  view: View;
  exceptions: string[];
  delivered_at: string | null;
  confirmed: { at: string; by: string } | null;
  reminder: Reminder | null;
  opened: number;
  downloads: number;
  /** Phase 0c: the provider's latest close-out of this visit day. */
  closed_out: Closeout | null;
  /** Residents whose audiogram is pending (not filed yet at close-out). */
  pending: number;
}

export interface Closeout { sequence: number; at: string; by_first_name: string; seen: number; noted: number; no_note: number; audiograms: number }

export interface Desk {
  ready: true;
  switches: Switches;
  config?: Config;
  can: Can;
  views: View[];
  summary_template: { version: string; approved: boolean };
  rows: DeskRow[];
  counts: Record<View, number>;
  capped: boolean;
  window_days: number;
}

export interface ResidentDocument {
  document_id: string;
  document_class: string | null;
  original_filename: string | null;
  content_type: string | null;
  byte_size: number;
  filed_at: string | null;
  test_date: string | null;
  on_merged_record: boolean;
  included: boolean;
  usable: boolean;
  problem: 'NOT_THIS_RESIDENTS' | 'NOT_DELIVERABLE' | 'NOT_AVAILABLE' | null;
}

export interface Deliverable { key: string; source_kind: SourceKind; document_id: string | null; uploaded: { at: string; pcc_date: string; by_first_name: string } | null }

export interface BatchResident {
  patient_id: string;
  first_name: string | null;
  last_name: string | null;
  room: string | null;
  merged_into: string | null;
  in_batch: boolean;
  basis: Array<'ROUNDS' | 'MANIFEST' | 'MANUAL'>;
  removed: { reason: string; at: string; by_first_name: string } | null;
  held: { reason: string; at: string; by_first_name: string } | null;
  flag: 'MOVED' | 'DECEASED' | null;
  flag_accepted: boolean;
  needs_decision: boolean;
  documents: ResidentDocument[];
  summary: { wanted: boolean; included: boolean; approved: boolean; note_id: string | null; note: { kind: 'NOTE' | 'NO_NOTE_NEEDED'; version: number; at: string; by_first_name: string; reason: string | null; written_by_first_name?: string; edited_by_first_name?: string | null } | null };
  missing: Array<'AUDIOGRAM' | 'VISIT_SUMMARY' | 'VISIT_NOTE'>;
  pending: Array<'AUDIOGRAM'>;
  complete: boolean;
  deliverables: Deliverable[];
}

export interface Recipient { id: string; primary: boolean; role: string | null; name: string | null; title?: string | null; email: string | null; problems: string[] }
/** How a building's recipients were confirmed: the contacts on file (ruling 2026-10-28) or a call-back to the main line. */
export type VerificationMethod = 'CONTACTS_ON_FILE' | 'CALLBACK_MAIN' | 'WITHDRAWN';

export interface SettingView {
  id: string;
  version: number;
  channel: Channel;
  recipient_contact_record_id: string | null;
  cc_contact_record_ids: string[];
  artifacts: Artifacts;
  format: string;
  cadence: string;
  weekly_day: number | null;
  confirmation_required: boolean;
  upload_is_confirmation: boolean;
  remind_after_business_days: number;
  escalate_after_business_days: number;
  pcc_document_category: string | null;
  pcc_filename_pattern: string | null;
  allow_webmail: boolean;
  webmail_reason: string | null;
  request_basis: string;
  note: string | null;
  set_by: { name: string; user_id: string };
  recorded_at: string;
}

export interface LinkView {
  link_id: string;
  attempt: number;
  created_at: string;
  expires_at: string;
  state: 'LIVE' | 'NOT_SENT' | 'REVOKED' | 'LOCKED' | 'EXPIRED' | 'RECIPIENT_CHANGED';
  revoked: { at: string; reason: string; code?: string | null; by_first_name: string } | null;
  opened: number;
  last_opened_at: string | null;
  code_failures: number;
  /** Recipient addresses locked by their own wrong codes (the link stays open for the others). */
  locked_addresses?: number;
  files: Array<{ file_no: number; source_kind: SourceKind; patient_id: string; document_id: string | null; byte_size: number; downloads: number; cap: number }>;
}

export interface Attempt { attempt: number; claimed_at: string | null; outcome: 'SENT' | 'FAILED' | 'UNKNOWN' | null; outcome_code: string | null; outcome_at: string | null; released: { outcome: string; at: string; by_first_name: string; note: string } | null; by_first_name: string | null }

export interface HistoryEntry { kind: 'ITEM' | 'DELIVERY'; event: string; at: string; by_first_name: string | null; patient_id?: string | null; method?: string | null; reason?: string | null; attempt?: number | null; occurred_on?: string | null }

export interface Batch {
  facility_id: string;
  facility_name: string | null;
  visit_date: string;
  batch_id: string | null;
  setting: { current: SettingView | null; recipients: Recipient[]; verified: boolean; verification: { event: string; method?: VerificationMethod | null; at: string; by_first_name: string } | null; exceptions: string[]; sendable: boolean };
  closure: { kind: string; reason: string; source: string } | null;
  residents: BatchResident[];
  counts: DeskRow['counts'];
  completed: { at: string; by_first_name: string } | null;
  all_complete: boolean;
  attempts: Attempt[];
  closeouts: Closeout[];
  closed_out: Closeout | null;
  send_held?: { reason: string; at: string } | null;
  /** The first-send delay (2026-10-28): a first send to a new recipient set waits 15 minutes, cancellable. */
  queued_send?: { event_id: string; queued_at: string; send_at: string; due: boolean; by_first_name: string | null } | null;
  first_send_cancelled?: { at: string; reason: string; by_first_name: string | null } | null;
  first_send_stopped?: { at: string; reason: string } | null;
  links: LinkView[];
  outside: Array<{ method: string; sent_on: string; note: string | null; at: string; by_first_name: string }>;
  confirmed: { at: string; method: string; confirmed_on: string; by: 'RECIPIENT_LINK' | 'VA_RECORDED'; by_first_name: string | null; note: string | null } | null;
  delivered_at: string | null;
  reminder: Reminder | null;
  queue: Queue;
  view: View;
  exceptions: string[];
  history: HistoryEntry[];
  can: Can;
  switches: Switches;
  config?: Config;
}

export interface SettingsScreen {
  facility_id: string;
  current: SettingView | null;
  recipients: Recipient[];
  verified: boolean;
  verification: { event: string; method?: VerificationMethod | null; at: string; by_first_name: string } | null;
  exceptions: string[];
  sendable: boolean;
  you_set_current: boolean;
  history: SettingView[];
  verifications: Array<{ event: string; at: string; by_first_name: string; note: string | null }>;
  contacts: Array<{
    id: string; role: string; name: string | null; title: string | null; email: string | null; webmail: boolean;
    /** The address's domain; true when no other current contact and no earlier recipient of the building uses it. */
    domain?: string | null; new_domain?: boolean;
    /** Created or changed in the last `recent_change_days` days (contacts are never edited in place). */
    recent_change?: { change: string; at: string; by_first_name: string; by_you: boolean } | null;
    /** The contact's note in Contacts (Gwynn's routing and exclusion notes); do_not_send when it says not to send there. */
    notes?: string | null; do_not_send?: boolean;
  }>;
  recent_change_days?: number;
  main_phone: { id: string; phone: string } | null;
  /** Copies allowed besides the recipient (five since 2026-10-28; older servers omit it: two). */
  limits?: { max_copies: number };
  vocabulary: { channels: Channel[]; reserved_channels: string[]; artifacts: Artifacts[]; cadences: string[] };
  can: Can;
  switches: Switches;
  config?: Config;
}

const enc = encodeURIComponent;
const batchPath = (facilityId: string, visitDate: string) => `${BASE}/batches/${enc(facilityId)}/${enc(visitDate)}`;
const residentPath = (facilityId: string, visitDate: string, patientId: string) => `${batchPath(facilityId, visitDate)}/residents/${enc(patientId)}`;
type Changed = { changed: boolean; [k: string]: unknown };

export const facilityRecordsApi = {
  desk: () => apiRequest<Desk>('GET', BASE),
  /** The latest provider close-out per building, 7 days: the shell's light read (never the whole desk). */
  closeouts: () => apiRequest<{ ready: true; closeouts: Array<{ facility_id: string; visit_date: string; at: string; by_first_name: string }> }>('GET', `${BASE}/closeouts`),
  batch: (f: string, d: string) => apiRequest<Batch>('GET', batchPath(f, d)),
  candidates: (f: string, d: string, q: string) => apiRequest<{ items: Array<{ patient_id: string; first_name: string | null; last_name: string | null; room: string | null }> }>('GET', `${batchPath(f, d)}/candidates?q=${enc(q)}`),
  addResident: (f: string, d: string, patientId: string, reason: string) => apiRequest<Changed>('POST', `${batchPath(f, d)}/residents`, { patient_id: patientId, reason }),
  removeResident: (f: string, d: string, p: string, reason: string) => apiRequest<Changed>('POST', `${residentPath(f, d, p)}/remove`, { reason }),
  hold: (f: string, d: string, p: string, hold: boolean, reason: string) => apiRequest<Changed>('POST', `${residentPath(f, d, p)}/hold`, { hold, reason }),
  acceptChange: (f: string, d: string, p: string, flag: string, reason: string) => apiRequest<Changed>('POST', `${residentPath(f, d, p)}/accept-change`, { flag, reason }),
  item: (f: string, d: string, p: string, body: { source_kind: SourceKind; document_id?: string | null; include: boolean; reason?: string }) => apiRequest<Changed>('POST', `${residentPath(f, d, p)}/items`, body),
  documentUrl: (f: string, d: string, p: string, documentId: string) => apiRequest<{ url: string; expires_in: number }>('GET', `${residentPath(f, d, p)}/documents/${enc(documentId)}/url`),
  summaryPreviewPath: (f: string, d: string, p: string) => `${residentPath(f, d, p)}/summary/pdf`,
  pccFilePath: (f: string, d: string, p: string, kind: SourceKind, documentId?: string | null) => `${residentPath(f, d, p)}/files/${kind}${documentId ? `/${enc(documentId)}` : ''}`,
  complete: (f: string, d: string) => apiRequest<Changed>('POST', `${batchPath(f, d)}/complete`, {}),
  reopen: (f: string, d: string, reason: string) => apiRequest<Changed>('POST', `${batchPath(f, d)}/reopen`, { reason }),
  outsideSent: (f: string, d: string, method: string, sentOn: string, note: string) => apiRequest<Changed>('POST', `${batchPath(f, d)}/outside-sent`, { method, sent_on: sentOn, note }),
  pccUploaded: (f: string, d: string, pccDate: string, items?: Array<{ patient_id: string; source_kind: SourceKind; document_id: string | null }>) => apiRequest<Changed>('POST', `${batchPath(f, d)}/pcc-uploaded`, { pcc_date: pccDate, items }),
  pccUndo: (f: string, d: string, item: { patient_id: string; source_kind: SourceKind; document_id: string | null }, reason: string) => apiRequest<Changed>('POST', `${batchPath(f, d)}/pcc-undo`, { ...item, reason }),
  confirmed: (f: string, d: string, method: string, confirmedOn: string, note: string) => apiRequest<Changed>('POST', `${batchPath(f, d)}/confirmed`, { method, confirmed_on: confirmedOn, note }),
  undoConfirmation: (f: string, d: string, reason: string) => apiRequest<Changed>('POST', `${batchPath(f, d)}/confirmation-undo`, { reason }),
  reminded: (f: string, d: string, method: string, note: string) => apiRequest<Changed>('POST', `${batchPath(f, d)}/reminded`, { method, note }),
  send: (f: string, d: string, settingVersion: number, expectedAttempts: number) => apiRequest<{ attempt?: number; outcome: string; file_count?: number; expires_at?: string; send_at?: string; already_queued?: boolean }>('POST', `${batchPath(f, d)}/send`, { setting_version: settingVersion, expected_attempts: expectedAttempts }),
  revoke: (f: string, d: string, linkId: string, reason: string) => apiRequest<Changed>('POST', `${batchPath(f, d)}/links/${enc(linkId)}/revoke`, { reason }),
  recall: (f: string, d: string, patientId: string, documentId: string | null, reason: string) => apiRequest<Changed>('POST', `${batchPath(f, d)}/recall`, { patient_id: patientId, document_id: documentId, reason }),
  cancelQueued: (f: string, d: string, reason: string) => apiRequest<Changed>('POST', `${batchPath(f, d)}/queued-send/cancel`, { reason }),
  sendQueuedNow: (f: string, d: string) => apiRequest<Changed>('POST', `${batchPath(f, d)}/queued-send/send-now`, {}),
  release: (f: string, d: string, attempt: number, outcome: 'WENT' | 'DID_NOT_GO', note: string) => apiRequest<Changed>('POST', `${batchPath(f, d)}/attempts/${attempt}/release`, { outcome, note }),
  settings: (f: string) => apiRequest<SettingsScreen>('GET', `${BASE}/settings/${enc(f)}`),
  saveSetting: (f: string, body: Record<string, unknown>) => apiRequest<{ version: number; links_revoked?: number; contacts_on_file?: boolean; sendable?: boolean }>('POST', `${BASE}/settings/${enc(f)}`, body),
  verify: (f: string, settingVersion: number, note: string) => apiRequest<Changed>('POST', `${BASE}/settings/${enc(f)}/verify`, { setting_version: settingVersion, note }),
  revokeVerification: (f: string, note: string) => apiRequest<Changed>('POST', `${BASE}/settings/${enc(f)}/verification/revoke`, { note }),
};

/** The words for every exception code the server sends; an unknown code is shown as itself. */
export const EXCEPTION_WORDS: Record<string, string> = {
  NO_SETTING: 'No records setting for this building',
  HOLD: 'Building set to HOLD: nothing is sent',
  MISSING_RECIPIENT: 'A contact changed: confirm the recipients again',
  WEBMAIL_REFUSED: 'Recipient on public webmail',
  RECIPIENT_UNVERIFIED: 'Recipients not confirmed yet',
  RESIDENT_MOVED: 'A resident moved since the visit',
  RESIDENT_DECEASED: 'A resident is recorded deceased',
  BUILDING_CLOSED: 'Building closed for providers',
  BUILDING_MERGED: 'Building merged into another',
  SEND_FAILED: 'The last send failed',
  SEND_UNKNOWN: 'A send may have gone: check Sent Items',
  LINK_EXPIRED_UNCONFIRMED: 'Link expired, not confirmed',
  LINK_LOCKED: 'Link locked after wrong codes',
  RECALL_NEEDED: 'A sent file was marked "Not this resident\'s"',
  LINKS_REVOKED_RECIPIENT_CHANGED: 'Links revoked: recipient changed',
  SEND_HELD_AT_CLOSEOUT: 'Close-out could not send: held for you',
  FIRST_SEND_STOPPED: 'First send stopped: see why below',
};
export const exceptionWords = (c: string) => EXCEPTION_WORDS[c] || c;

export const VIEW_LABEL: Record<View, string> = {
  to_prepare: 'To prepare',
  ready_to_send: 'Ready to send',
  to_upload: 'To upload to PCC',
  awaiting_confirmation: 'Awaiting confirmation',
  exceptions: 'Exceptions',
  done: 'Done',
};

export const CHANNEL_LABEL: Record<Channel, string> = {
  EMAIL: 'Secure link by email',
  PCC_UPLOAD: 'Upload to PCC (assisted)',
  OTHER: 'Sent outside eMerge',
  HOLD: 'Hold (send nothing)',
};

export const refusalWords = (r: { status?: number; code?: string; message?: string; error?: string }, fallback: string): string => {
  if (r.status === undefined && !r.code) return 'Could not reach eMerge. Nothing was changed.';
  return r.message || r.error || fallback;
};
